import RefreshIcon from '@mui/icons-material/Refresh'
import { LoadingButton } from '@mui/lab'
import { Grid, InputAdornment, TextField } from '@mui/material'
import Decimal from 'decimal.js'
import { Controller, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import { ControlledTextField } from 'components/common'
import {
  GetDistanceCalculationQuery,
  GoogleDistanceStatus,
  useGetDistanceCalculationLazyQuery,
} from 'generated/graphql'
import { getGoogleAddressInput } from 'helpers'
import { sanitizedNumberField } from 'helpers/sanitizeNumberInput.helpers'
import { usePermission } from 'providers'
import { TBookingForm } from 'types/form-types'

interface IDistanceInputProps {
  isQuickQuote: boolean
  disabled?: boolean
}

const DistanceInput = (props: IDistanceInputProps) => {
  const { isQuickQuote, disabled } = props

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<TBookingForm>()

  const [isAllowedToMakeBookingDistanceFieldReadonly] = usePermission(
    'MakeBookingDistanceFieldReadonly',
  )

  // Queries
  const handleDistanceCalculation = (
    data: GetDistanceCalculationQuery,
    distanceType: 'distance' | 'returnDistance',
  ) => {
    const { status, distance: rawDistance, errorMessage } = data.distanceCalculation

    if (status === GoogleDistanceStatus.Success) {
      const distance = new Decimal(rawDistance).toDP(2, Decimal.ROUND_FLOOR).toNumber()

      setValue(distanceType, distance, { shouldDirty: true, shouldValidate: true })
    } else if (status === GoogleDistanceStatus.Error) {
      toast.warning(errorMessage)
    }
  }

  const [getDistanceCalculation, { loading: distanceLoading }] = useGetDistanceCalculationLazyQuery(
    {
      onError: (error) => {
        toast.error(error.message)
      },
    },
  )

  // Handlers
  const handleRefresh = () => {
    const collectionAddressesValue = getValues().collectionAddresses
    const deliveryAddressesValue = getValues().deliveryAddresses

    if (collectionAddressesValue && deliveryAddressesValue) {
      const isWaitAndReturn = getValues('isWaitAndReturn') ?? false
      const googleAddressInput = getGoogleAddressInput(
        collectionAddressesValue,
        deliveryAddressesValue,
      )

      getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
        ({ data }) => data && handleDistanceCalculation(data, 'distance'),
      )

      if (isWaitAndReturn) {
        const lastTwoAddresses = googleAddressInput.slice(-2)
        getDistanceCalculation({ variables: { input: lastTwoAddresses } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
        )
      } else {
        setValue('returnDistance', 0, { shouldDirty: true })
      }
    }
  }

  return (
    <Grid container columnSpacing={0.5} justifyContent='end' alignItems='center'>
      {isAllowedToMakeBookingDistanceFieldReadonly || isQuickQuote ? (
        <Grid item xs={7}>
          <ControlledTextField
            control={control}
            name='distance'
            label='Distance'
            disabled={disabled}
            readOnly
            size='small'
            endAdornment='mi'
          />
        </Grid>
      ) : (
        <Grid item xs={7}>
          <Controller
            control={control}
            name='distance'
            defaultValue={0}
            render={({ field: { onBlur, onChange, ...restField } }) => (
              <TextField
                {...restField}
                label='Distance'
                disabled={disabled}
                required
                type='text'
                size='small'
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position='start'>mi</InputAdornment>,
                }}
                error={!!errors.distance}
                helperText={errors.distance?.message}
                onBlur={() => {
                  onBlur()
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={(e) => sanitizedNumberField(e.target.value, onChange)}
              />
            )}
          />
        </Grid>
      )}

      <Grid item>
        <LoadingButton loading={distanceLoading} disabled={disabled} onClick={handleRefresh}>
          <RefreshIcon />
        </LoadingButton>
      </Grid>
    </Grid>
  )
}

export default DistanceInput
